import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { 
  ArrowLeft, 
  Shield, 
  Lock, 
  Server, 
  Eye, 
  Database, 
  Key,
  Globe,
  FileCheck,
  Trash2,
  UserCheck,
  AlertTriangle,
  ExternalLink
} from "lucide-react";
import { useNavigate, Link } from "react-router-dom";
import { HelpCircle } from "lucide-react";
import { useTranslation } from "@/lib/i18n/useTranslation";
import { SEO } from "@/components/seo/SEO";

const Privacy = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();

  return (
    <div className="min-h-screen bg-background">
      <SEO 
        title="Politique de confidentialité"
        description="Découvrez comment EasyFinder protège vos données : chiffrement AES-256, serveurs européens, conformité RGPD. Votre vie privée est notre priorité."
        canonical="/privacy"
      />
      <header className="sticky top-0 z-40 backdrop-blur-xl bg-background/80 border-b border-border">
        <div className="container mx-auto px-4 h-16 flex items-center gap-4">
          <Button variant="ghost" size="icon" onClick={() => navigate(-1)}>
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <h1 className="text-xl font-semibold">{t.privacy.title}</h1>
        </div>
      </header>

      <main className="container mx-auto px-4 py-12 max-w-4xl">
        {/* Security Highlights */}
        <div className="mb-12">
          <h2 className="text-2xl font-bold text-center mb-2">{t.privacy.highlights.title}</h2>
          <p className="text-muted-foreground text-center mb-8">
            {t.privacy.highlights.subtitle}
          </p>
          
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <Card className="border-emerald-500/20 bg-emerald-500/5">
              <CardContent className="p-4 text-center">
                <div className="w-12 h-12 mx-auto mb-3 rounded-full bg-emerald-500/10 flex items-center justify-center">
                  <Shield className="w-6 h-6 text-emerald-600" />
                </div>
                <h3 className="font-semibold text-sm mb-1">{t.privacy.highlights.aes256}</h3>
                <p className="text-xs text-muted-foreground">{t.privacy.highlights.aes256Desc}</p>
              </CardContent>
            </Card>
            
            <Card className="border-blue-500/20 bg-blue-500/5">
              <CardContent className="p-4 text-center">
                <div className="w-12 h-12 mx-auto mb-3 rounded-full bg-blue-500/10 flex items-center justify-center">
                  <Globe className="w-6 h-6 text-blue-600" />
                </div>
                <h3 className="font-semibold text-sm mb-1">{t.privacy.highlights.euServers}</h3>
                <p className="text-xs text-muted-foreground">{t.privacy.highlights.euServersDesc}</p>
              </CardContent>
            </Card>
            
            <Card className="border-purple-500/20 bg-purple-500/5">
              <CardContent className="p-4 text-center">
                <div className="w-12 h-12 mx-auto mb-3 rounded-full bg-purple-500/10 flex items-center justify-center">
                  <Eye className="w-6 h-6 text-purple-600" />
                </div>
                <h3 className="font-semibold text-sm mb-1">{t.privacy.highlights.noSale}</h3>
                <p className="text-xs text-muted-foreground">{t.privacy.highlights.noSaleDesc}</p>
              </CardContent>
            </Card>
            
            <Card className="border-orange-500/20 bg-orange-500/5">
              <CardContent className="p-4 text-center">
                <div className="w-12 h-12 mx-auto mb-3 rounded-full bg-orange-500/10 flex items-center justify-center">
                  <Lock className="w-6 h-6 text-orange-600" />
                </div>
                <h3 className="font-semibold text-sm mb-1">{t.privacy.highlights.exclusiveAccess}</h3>
                <p className="text-xs text-muted-foreground">{t.privacy.highlights.exclusiveAccessDesc}</p>
              </CardContent>
            </Card>
          </div>
        </div>

        <article className="prose prose-slate max-w-none">
          {/* Limited Use Declaration - MANDATORY for Google OAuth */}
          <section className="mb-8 p-6 bg-indigo-500/10 rounded-xl border-2 border-indigo-500/30">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 rounded-full bg-indigo-500/20 flex items-center justify-center">
                <Shield className="w-5 h-5 text-indigo-600" />
              </div>
              <h2 className="text-2xl font-semibold">{t.privacy.googleApi.title}</h2>
            </div>
            
            <p className="text-muted-foreground leading-relaxed mb-4">
              {t.privacy.googleApi.intro}{" "}
              <a 
                href="https://developers.google.com/terms/api-services-user-data-policy" 
                target="_blank" 
                rel="noopener noreferrer"
                className="text-primary hover:underline inline-flex items-center gap-1"
              >
                {t.privacy.googleApi.policy}
                <ExternalLink className="w-3 h-3" />
              </a>
              {t.privacy.googleApi.including}{" "}
              <a 
                href="https://developers.google.com/terms/api-services-user-data-policy#additional_requirements_for_specific_api_scopes" 
                target="_blank" 
                rel="noopener noreferrer"
                className="text-primary hover:underline inline-flex items-center gap-1"
              >
                {t.privacy.googleApi.limitedUse}
                <ExternalLink className="w-3 h-3" />
              </a>.
            </p>
            
            <div className="bg-background/50 p-4 rounded-lg border border-indigo-500/20">
              <p className="font-semibold text-sm mb-3">{t.privacy.googleApi.commitments}</p>
              <ul className="space-y-2 text-sm text-muted-foreground">
                {t.privacy.googleApi.items.map((item, index) => (
                  <li key={index} className="flex items-start gap-2">
                    <Shield className="w-4 h-4 text-indigo-600 mt-0.5 flex-shrink-0" />
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
            </div>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold mb-4">{t.privacy.sections.intro.title}</h2>
            <p className="text-muted-foreground leading-relaxed">
              {t.privacy.sections.intro.content}
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold mb-4">{t.privacy.sections.dataCollected.title}</h2>
            <p className="text-muted-foreground leading-relaxed mb-4">{t.privacy.sections.dataCollected.intro}</p>
            <ul className="list-disc pl-6 space-y-2 text-muted-foreground">
              {t.privacy.sections.dataCollected.items.map((item, index) => (
                <li key={index}>{item}</li>
              ))}
            </ul>
          </section>

          {/* Security Details Section */}
          <section className="mb-8 p-6 bg-muted/30 rounded-xl border border-border">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center">
                <Key className="w-5 h-5 text-primary" />
              </div>
              <h2 className="text-2xl font-semibold">{t.privacy.sections.security.title}</h2>
            </div>
            
            <p className="text-muted-foreground leading-relaxed mb-6">
              {t.privacy.sections.security.intro}
            </p>
            
            <div className="grid md:grid-cols-2 gap-4">
              <div className="flex items-start gap-3">
                <Database className="w-5 h-5 text-emerald-600 mt-0.5 flex-shrink-0" />
                <div>
                  <h4 className="font-medium text-sm">{t.privacy.sections.security.encryptionAtRest}</h4>
                  <p className="text-xs text-muted-foreground">
                    {t.privacy.sections.security.encryptionAtRestDesc}
                  </p>
                </div>
              </div>
              
              <div className="flex items-start gap-3">
                <Lock className="w-5 h-5 text-blue-600 mt-0.5 flex-shrink-0" />
                <div>
                  <h4 className="font-medium text-sm">{t.privacy.sections.security.encryptionInTransit}</h4>
                  <p className="text-xs text-muted-foreground">
                    {t.privacy.sections.security.encryptionInTransitDesc}
                  </p>
                </div>
              </div>
              
              <div className="flex items-start gap-3">
                <Server className="w-5 h-5 text-purple-600 mt-0.5 flex-shrink-0" />
                <div>
                  <h4 className="font-medium text-sm">{t.privacy.sections.security.secureInfra}</h4>
                  <p className="text-xs text-muted-foreground">
                    {t.privacy.sections.security.secureInfraDesc}
                  </p>
                </div>
              </div>
              
              <div className="flex items-start gap-3">
                <UserCheck className="w-5 h-5 text-orange-600 mt-0.5 flex-shrink-0" />
                <div>
                  <h4 className="font-medium text-sm">{t.privacy.sections.security.accessControl}</h4>
                  <p className="text-xs text-muted-foreground">
                    {t.privacy.sections.security.accessControlDesc}
                  </p>
                </div>
              </div>
            </div>
          </section>

          {/* AI Data Handling */}
          <section className="mb-8 p-6 bg-blue-500/5 rounded-xl border border-blue-500/20">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 rounded-full bg-blue-500/10 flex items-center justify-center">
                <FileCheck className="w-5 h-5 text-blue-600" />
              </div>
              <h2 className="text-2xl font-semibold">{t.privacy.sections.ai.title}</h2>
            </div>
            
            <p className="text-muted-foreground leading-relaxed mb-4">
              {t.privacy.sections.ai.intro}
            </p>
            
            <ul className="space-y-3 text-muted-foreground">
              {t.privacy.sections.ai.items.map((item, index) => (
                <li key={index} className="flex items-start gap-2">
                  <Shield className="w-4 h-4 text-blue-600 mt-0.5 flex-shrink-0" />
                  <span className="text-sm">{item}</span>
                </li>
              ))}
            </ul>
          </section>

          {/* Gmail Integration Section - Required for Google OAuth validation */}
          <section className="mb-8 p-6 bg-red-500/5 rounded-xl border border-red-500/20">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 rounded-full bg-red-500/10 flex items-center justify-center">
                <svg className="w-5 h-5 text-red-600" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M24 5.457v13.909c0 .904-.732 1.636-1.636 1.636h-3.819V11.73L12 16.64l-6.545-4.91v9.273H1.636A1.636 1.636 0 0 1 0 19.366V5.457c0-2.023 2.309-3.178 3.927-1.964L5.455 4.64 12 9.548l6.545-4.91 1.528-1.145C21.69 2.28 24 3.434 24 5.457z"/>
                </svg>
              </div>
              <h2 className="text-2xl font-semibold">{t.privacy.sections.gmail.title}</h2>
            </div>
            
            <p className="text-muted-foreground leading-relaxed mb-4">
              {t.privacy.sections.gmail.intro}
            </p>
            
            <div className="space-y-4">
              <div className="p-4 bg-emerald-500/10 rounded-lg border border-emerald-500/20">
                <h4 className="font-semibold text-emerald-700 dark:text-emerald-400 mb-2 flex items-center gap-2">
                  <Shield className="w-4 h-4" /> {t.privacy.sections.gmail.whatWeImport}
                </h4>
                <ul className="space-y-1 text-sm text-muted-foreground">
                  {t.privacy.sections.gmail.whatWeImportItems.map((item, index) => (
                    <li key={index}>• {item}</li>
                  ))}
                </ul>
              </div>
              
              <div className="p-4 bg-red-500/10 rounded-lg border border-red-500/20">
                <h4 className="font-semibold text-red-700 dark:text-red-400 mb-2 flex items-center gap-2">
                  <AlertTriangle className="w-4 h-4" /> {t.privacy.sections.gmail.whatWeNeverDo}
                </h4>
                <ul className="space-y-1 text-sm text-muted-foreground">
                  {t.privacy.sections.gmail.whatWeNeverDoItems.map((item, index) => (
                    <li key={index}>• <strong>{item}</strong></li>
                  ))}
                </ul>
              </div>
              
              <div className="p-4 bg-blue-500/10 rounded-lg border border-blue-500/20">
                <h4 className="font-semibold text-blue-700 dark:text-blue-400 mb-2 flex items-center gap-2">
                  <Key className="w-4 h-4" /> {t.privacy.sections.gmail.oauthSecurity}
                </h4>
                <ul className="space-y-1 text-sm text-muted-foreground">
                  {t.privacy.sections.gmail.oauthSecurityItems.map((item, index) => (
                    <li key={index}>• {item}</li>
                  ))}
                </ul>
              </div>
              
              <div className="p-4 bg-purple-500/10 rounded-lg border border-purple-500/20">
                <h4 className="font-semibold text-purple-700 dark:text-purple-400 mb-2 flex items-center gap-2">
                  <Database className="w-4 h-4" /> {t.privacy.sections.gmail.dataStored}
                </h4>
                <ul className="space-y-1 text-sm text-muted-foreground">
                  {t.privacy.sections.gmail.dataStoredItems.map((item, index) => (
                    <li key={index}>• {item}</li>
                  ))}
                </ul>
              </div>
            </div>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold mb-4">{t.privacy.sections.dataUsage.title}</h2>
            <p className="text-muted-foreground leading-relaxed mb-4">{t.privacy.sections.dataUsage.intro}</p>
            <ul className="list-disc pl-6 space-y-2 text-muted-foreground">
              {t.privacy.sections.dataUsage.items.map((item, index) => (
                <li key={index}>{item}</li>
              ))}
            </ul>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold mb-4">{t.privacy.sections.dataSharing.title}</h2>
            <p className="text-muted-foreground leading-relaxed mb-4">
              <strong className="text-foreground">{t.privacy.sections.dataSharing.intro}</strong> {t.privacy.sections.dataSharing.introSub}
            </p>
            <ul className="list-disc pl-6 space-y-2 text-muted-foreground">
              {t.privacy.sections.dataSharing.items.map((item, index) => (
                <li key={index}>{item}</li>
              ))}
            </ul>
            <p className="text-muted-foreground leading-relaxed mt-4">
              {t.privacy.sections.dataSharing.footer}
            </p>
          </section>

          {/* Data Retention */}
          <section className="mb-8 p-6 bg-orange-500/5 rounded-xl border border-orange-500/20">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 rounded-full bg-orange-500/10 flex items-center justify-center">
                <Trash2 className="w-5 h-5 text-orange-600" />
              </div>
              <h2 className="text-2xl font-semibold">{t.privacy.sections.retention.title}</h2>
            </div>
            
            <ul className="space-y-3 text-muted-foreground">
              {t.privacy.sections.retention.items.map((item, index) => (
                <li key={index} className="flex items-start gap-2">
                  <span className="text-sm">{item}</span>
                </li>
              ))}
            </ul>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold mb-4">{t.privacy.sections.gdpr.title}</h2>
            <p className="text-muted-foreground leading-relaxed mb-4">
              {t.privacy.sections.gdpr.intro}
            </p>
            <ul className="list-disc pl-6 space-y-2 text-muted-foreground">
              {t.privacy.sections.gdpr.items.map((item, index) => (
                <li key={index}>{item}</li>
              ))}
            </ul>
            <p className="text-muted-foreground leading-relaxed mt-4">
              {t.privacy.sections.gdpr.footer}
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold mb-4">{t.privacy.sections.cookies.title}</h2>
            <p className="text-muted-foreground leading-relaxed">
              {t.privacy.sections.cookies.content}
            </p>
          </section>

          {/* Contact Section */}
          <section className="mb-8 p-6 bg-primary/5 rounded-xl border border-primary/20">
            <h2 className="text-2xl font-semibold mb-4">{t.privacy.sections.contact.title}</h2>
            <p className="text-muted-foreground leading-relaxed mb-4">
              {t.privacy.sections.contact.intro}
            </p>
            <ul className="space-y-2 text-muted-foreground">
              <li>
                <strong>{t.privacy.sections.contact.email}</strong>{" "}
                <a href="mailto:contact@easyfinder.fr" className="text-primary hover:underline">
                  contact@easyfinder.fr
                </a>
              </li>
              <li>
                <strong>{t.privacy.sections.contact.responseTime}</strong>
              </li>
            </ul>
            <p className="text-muted-foreground leading-relaxed mt-4 text-sm">
              {t.privacy.sections.contact.cnil}
              <a href="https://www.cnil.fr" target="_blank" rel="noopener noreferrer" className="text-primary hover:underline ml-1">
                www.cnil.fr
              </a>
            </p>
          </section>

          {/* FAQ Link */}
          <Link 
            to="/security-faq" 
            className="flex items-center justify-center gap-2 p-4 bg-muted/30 rounded-xl border border-border hover:border-primary/30 hover:bg-muted/50 transition-all group"
          >
            <HelpCircle className="w-5 h-5 text-primary" />
            <span className="font-medium">{t.privacy.faqLink}</span>
            <ArrowLeft className="w-4 h-4 text-muted-foreground rotate-180 group-hover:translate-x-1 transition-transform" />
          </Link>
        </article>
      </main>
    </div>
  );
};

export default Privacy;
